import React, { useEffect } from 'react'
import { useDispatch, batch } from 'react-redux'

import AccommodationIcon from '../../icons/accommodation.svg'
import CostIcon from '../../icons/euro.svg'
import CrossHairs from '../../icons/crosshairs.svg'
import DeviationIcon from '../../icons/deviation.svg'
import PlusIcon from '../../icons/plus.svg'
import AwardIcon from '../../icons/award.svg'
import RatingIcon from '../../icons/star.svg'
import SortIcon from '../../icons/sort.svg'

import TrailNavigation from '../../components/TrailNavigation'
import Distance from '../../components/Distance'
import Image from '../../components/Image'
import Loading from '../../components/Loading'
import MetaData from '../../components/MetaData'
import PlaceHolder from '../../components/PlaceHolder'
import Stats from '../../components/Stats'
import Title from '../../components/Title'
import UserPositionControl from '../../components/UserPositionControl'
import UserPositionManager from '../../components/UserPositionManager'
import { IconList } from '../../components/Icons'
import { isTouchDevice, range } from '../../utils'

import {
  setProfilePin
} from '../../slices/profile'

import {
  showMapPanel,
  useIsMobile,
  usePagination,
  showSidebar,
  setInfoPanel,
  useHaveInfoPanel,
  addMessage
} from '../../slices/ui'

import {
  useAccommodationOrder,
  useAccommodationAvailability
} from '../../slices/accommodation'

import { reverse, absoluteUrl } from '../../urls'
import { serializeAvailability } from '../../location'

import {
  addMapFeatures,
  removeMapFeatures,
  setMapExtent,
  setMapPin
} from '../../slices/map'

import {
  useAccommodations,
  useRoute,
  useRouteNearestTrail,
  useStep,
  useTrail,
  useWaypoint
} from '../../api'

import {
  useLocation,
  useQuadhashStats,
  useUrlParams,
  useNavigatorOnline
} from '../../hooks'

import {
  BOOK_A_ROOM,
  CHANGE_DATE,
  CHECK_AVAILABILITY,
  DISTANCE_FROM_TRAIL,
  GO_TO_PLACE,
  LOAD_MORE,
  NO_ROOMS_AVAILABLE,
  PRICE_RANGE,
  SHOW_ON_MAP,
  SORT,
  USER_RATING,
  VIEW_ON_BOOKINGCOM,
  WHERE_TO_SLEEP,
  WHERE_TO_SLEEP_DESCRIPTION,
  WHERE_TO_SLEEP_IN
} from './../../trans'

const AccommodationStats = (props) => {
  const { accommodation } = props
  const { route_id, trail_id = 1 } = useUrlParams(['trail_id', 'route_id'])
  const { length, time } = useQuadhashStats({ quadhash: accommodation.quadhash, route_id: route_id, trail_id: trail_id })
  if (!trail_id) return null
  return <Stats length={length} time={time} elevation={accommodation.elevation} />
}

const DistanceFromTrail = (props) => {
  const { quadhash } = props
  const { route_id } = useUrlParams(['route_id'])
  const { distance_from_trail } = useRouteNearestTrail({
    routeId: route_id,
    quadhash: quadhash
  }, { skip: !quadhash }) || {}

  if (!distance_from_trail || distance_from_trail < 250) return null

  return (
    <span className="pwa__list__deviation fade-in">
      <DeviationIcon/>
      <Distance value={distance_from_trail}/>
      &nbsp;
      { DISTANCE_FROM_TRAIL }
    </span>
  )
}

const Accommodation = React.memo((props) => {
  const { accommodation, index, className = '' } = props
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const onMouseEnter = !!accommodation.id && !isTouchDevice && !isMobile
    ? (e) => batch(() => {
        dispatch(setMapPin(accommodation.lnglat))
        if (!accommodation.is_deviation) {
          dispatch(setProfilePin([
            accommodation.length,
            accommodation.elevation_on_trail,
            accommodation.time]))
        }
      })
    : undefined

  const onMouseLeave = !!accommodation.id && !isTouchDevice && !isMobile
    ? (e) => batch(() => {
        dispatch(setMapPin(null))
        dispatch(setProfilePin(null))
      })
    : undefined

  const onClick = accommodation.id
    ? (e) => batch(() => {
        dispatch(showMapPanel(true))
        dispatch(setMapExtent({ bounds: accommodation.extent }))
        dispatch(setInfoPanel({
          type: 'accommodation',
          id: accommodation.id
        }))
      })
    : undefined

  const onActionClick = accommodation.id
    ? (e) => batch(() => {
        e.stopPropagation()
        dispatch(showMapPanel(true))
        dispatch(setMapExtent({ bounds: accommodation.extent }))
        dispatch(setMapPin(accommodation.lnglat))
      })
    : undefined

  const stopPropagation = (e) => e.stopPropagation()

  return (
    <li className={className + ' pwa__list__item fade-in'}
      style={{ animationDelay: `${0.1 + 0.03 * index}s` }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>

      { accommodation.affiliate_url && (
        <a href={accommodation.booking_url || accommodation.affiliate_url}
          onClick={stopPropagation}
          className="pwa__list__image"
          referrerPolicy="origin"
          rel="noopener nofollow noreferrer"
          target="_blank">
          { accommodation.image && <Image src={ accommodation.image.small }
            fallback={<AccommodationIcon/>} /> }
          <div className="pwa__list__image__action">
            { accommodation.booking_url ? BOOK_A_ROOM : VIEW_ON_BOOKINGCOM }
          </div>
          { accommodation.current_price && (
            <div className="pwa__badge positive">
              { accommodation.current_price }
            </div>
          )}
          { !accommodation.current_price && accommodation.ourpick && (
            <div className="pwa__accommodation__ourpick">
              <AwardIcon/>
            </div>
          )}
        </a>
      ) }

      { !accommodation.affiliate_url && (
        <div className="pwa__list__image">
          { accommodation.image && <Image src={ accommodation.image.small }
            fallback={<AccommodationIcon/>} /> }
        </div>
      )}

      <div title={ `${GO_TO_PLACE} ${accommodation.title}`} className="pwa__list__container">

        <h3 className="pwa__list__title inherit">
          <PlaceHolder value={accommodation.title}>
            {accommodation.title}
            <span className="pwa__list__subtitle">
              {accommodation.subtitle && `, ${accommodation.subtitle}`}
            </span>
          </PlaceHolder>
        </h3>

        <DistanceFromTrail quadhash={accommodation.quadhash} />

        <div className="pwa__details">
          <PlaceHolder value={accommodation.cost_range || accommodation.rating} transparent={true}>
            { accommodation.cost_range && (
              <abbr title={PRICE_RANGE} className="pwa__costrange">
                { range(1, accommodation.cost_range).map((x) => <CostIcon key={x}/>) }
              </abbr>
            ) }
            { accommodation.rating && accommodation.cost_range && (<>/</>) }
            { accommodation.rating && (
              <abbr title={USER_RATING} className="pwa__rating">
                { range(1, accommodation.rating).map((x) => <RatingIcon key={x}/>) }
              </abbr>
            ) }
          </PlaceHolder>
        </div>

        <div className='pwa__services__list'>
          <PlaceHolder value={accommodation.id}>
            {accommodation.services && <IconList icons={accommodation.services} inlineText={true} />}
          </PlaceHolder>
        </div>

        <AccommodationStats accommodation={accommodation} />

      </div>

      <div className="pwa__list__actions">
        <PlaceHolder value={accommodation.quadhash}>
          <button className="button circular pwa__list__action" onClick={onActionClick}>
            <abbr title={SHOW_ON_MAP} className="pwa__action__icon">
              <CrossHairs/>
            </abbr>
          </button>
        </PlaceHolder>
      </div>
    </li>
  )
})

Accommodation.displayName = 'Accommodation'

const AccommodationList = (props) => {
  const { accommodationList, order, page, availability } = props
  const { isOnline } = useNavigatorOnline()
  const [currentPage, setPage] = usePagination('accommodationPage')
  const dispatch = useDispatch()
  const apiArgs = Object.assign({}, accommodationList, {
    routeId: accommodationList.route_id,
    page: page,
    order: order
  })
  const apiOptions = availability ? { keepOld: true } : {}
  switch (accommodationList.type) {
    case 'trail':
      apiArgs.trailId = accommodationList.trail_id
      break
    case 'step':
      apiArgs.trailId = accommodationList.trail_id
      apiArgs.stepId = accommodationList.step_id
      break
    case 'waypoint':
      apiArgs.quadhash = accommodationList.quadhash
      break
    default:
      throw new Error(`invalid type ${accommodationList.type}`)
  }
  if (availability && isOnline) {
    apiArgs.availability = serializeAvailability(availability)
  }
  const query = useAccommodations(apiArgs, apiOptions)
  const { objects, has_next } = query || {}
  const noRommsAvailabile = !!availability && isOnline && page === 1 && objects && !objects.some((x) => !!x.booking_url)
  const loadMore = has_next && isOnline && currentPage === page
  const accommodations = objects || new Array(10).fill({})
  const items = accommodations.map(
    (accommodation, index) => <Accommodation accommodation={accommodation}
                                             key={page + index}
                                             index={index} />)

  useEffect(() => {
    if (!noRommsAvailabile) return
    dispatch(addMessage({
      message: NO_ROOMS_AVAILABLE,
      timeout: 5,
      status: 'info'
    }))
  }, [noRommsAvailabile, dispatch])

  useEffect(() => {
    const features = []
    if (!objects) return
    for (const object of objects) {
      features.push({
        id: object.id,
        type: 'accommodation',
        icons: ['accommodation'],
        lnglat: object.lnglat,
        label: object.current_price || '',
        color: object.current_price ? '#2BA648' : '#294B62'
      })
    }
    dispatch(addMapFeatures(features))
    return () => {
      if (!features) return
      dispatch(removeMapFeatures(features))
    }
  }, [objects, dispatch])

  return (
    <>
      { items }
      { loadMore && (
        <button onClick={() => dispatch(setPage(page + 1))}
          className="button basic pwa__alternative__stop ">
          <PlusIcon aria-hidden={true}/>
          { LOAD_MORE }
        </button>
      )}
    </>
  )
}

const AccommodationTrailNavigation = (props) => {
  const { route_id, route, trail, accommodationList } = props
  const prevTrail = trail.trail_id > 1
    ? reverse(route_id, trail.trail_id - 1, Object.assign({}, accommodationList, { trail_id: trail.trail_id - 1 }))
    : null
  const nextTrail = trail.trail_id < route.trail_count
    ? reverse(route_id, trail.trail_id + 1, Object.assign({}, accommodationList, { trail_id: trail.trail_id + 1 }))
    : null
  const backLink = reverse(route_id)
  return <TrailNavigation prevTrail={prevTrail} nextTrail={nextTrail} backLink={backLink} />
}

const AccommodationPage = () => {
  const { route_id, trail_id, accommodationList } = useUrlParams()
  const { isOnline } = useNavigatorOnline()
  const location = useLocation()[0]
  const page = usePagination('accommodationPage')[0]
  const dispatch = useDispatch()
  const haveInfoPanel = useHaveInfoPanel()
  const route = useRoute({ routeId: route_id })
  const step = useStep({
    routeId: accommodationList.route_id,
    trailId: accommodationList.trail_id,
    stepId: accommodationList.step_id
  }, { skip: accommodationList.type !== 'step' }) || {}
  const waypoint = useWaypoint(
    { quadhash: accommodationList.quadhash },
    { skip: accommodationList.type !== 'waypoint' }) || {}
  const trail = useTrail({
    routeId: route_id,
    trailId: accommodationList.trail_id
  }, { skip: accommodationList.type !== 'trail' }) || {}
  const accommodationOrder = useAccommodationOrder()
  const availability = useAccommodationAvailability()

  if (!route) return (<Loading/>)

  const titleLink = trail ? reverse(route_id, (trail_id || 1)) : reverse(route_id)
  const key = `${trail_id}-${accommodationOrder}-${JSON.stringify(accommodationList)}-${JSON.stringify(availability)}`
  const title = trail.title || route.title
  const placeTitle = (waypoint?.title || step?.title)
  const subtitle = placeTitle ? `${WHERE_TO_SLEEP_IN} ${waypoint.title || step.title}` : WHERE_TO_SLEEP
  const description = `${WHERE_TO_SLEEP_IN} ${placeTitle || trail?.stop_place?.title}. ${WHERE_TO_SLEEP_DESCRIPTION}`
  const is_bookable = trail.is_bookable || step.is_bookable || waypoint.is_bookable
  const pages = Math.ceil(trail.accommodation_count / 10)
  const canonicalURL = (pages > 1) ? absoluteUrl(location + '?p=accommodationPage-' + pages) : null

  return (
    <>
      { !haveInfoPanel && (
        <MetaData title={subtitle}
                  description={description}
                  canonicalURL={canonicalURL} />
      )}

      <Title title={title} subtitle={subtitle} titlelink={titleLink} className="options">
        <button className="button pwa__header__button basic"
            disabled={!isOnline}
            onClick={() => dispatch(showSidebar('accommodationorder')) }>
            <SortIcon/>
            {SORT}
        </button>
      </Title>

      { accommodationList.type === 'trail' && <AccommodationTrailNavigation route_id={route_id}
                                                                             trail_id={trail_id}
                                                                             route={route}
                                                                             trail={trail}
                                                                             accommodationList={accommodationList} />
      }

      <ol className="pwa__list image hover">
        { range(1, page).map((p) =>
        <AccommodationList routeId={route_id}
          accommodationList={accommodationList}
          availability={availability}
          order={accommodationOrder}
          page={p}
          key={`${key}-${p}`}/>
        )}
      </ol>

      <UserPositionControl/>
      <UserPositionManager/>

      { is_bookable && (
        <button className="button primary pwa__footer_button fade-in"
          disabled={!isOnline}
          onClick={() => dispatch(showSidebar('reservation'))}>
          { availability ? CHANGE_DATE : CHECK_AVAILABILITY }
        </button>
      )}

    </>
  )
}

export default AccommodationPage
